import { useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Github } from "lucide-react";
import { PROJECTS, getProject } from "@/data/projects";
import { MaskLines, Reveal } from "@/components/Primitives";
import { EASE, usePageMeta } from "@/lib/motion";
import NotFound from "@/pages/NotFound";

const Block = ({ n, title, children, testId }) => (
  <Reveal className="grid grid-cols-1 gap-6 border-t border-line py-14 lg:grid-cols-12" data-testid={testId}>
    <div className="lg:col-span-4">
      <span className="eyebrow">{n}</span>
      <h2 className="mt-3 font-serif text-3xl font-light text-ivory sm:text-4xl">{title}</h2>
    </div>
    <div className="text-base leading-relaxed text-ash sm:text-lg lg:col-span-7 lg:col-start-6">{children}</div>
  </Reveal>
);

const LinkOrPending = ({ href, label, Icon, testId }) =>
  href ? (
    <a href={href} target="_blank" rel="noopener noreferrer" className="btn btn-ghost" data-testid={testId}>
      <Icon size={16} /> {label}
    </a>
  ) : (
    <span className="inline-flex min-h-[48px] items-center gap-3 border border-dashed border-line px-6 font-mono text-xs uppercase tracking-[0.2em] text-ash" data-testid={`${testId}-pending`}>
      <Icon size={16} /> {label} — coming soon
    </span>
  );

const Detail = ({ p }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);
  const next = PROJECTS[(PROJECTS.indexOf(p) + 1) % PROJECTS.length];
  usePageMeta(`${p.title} — Hades`, p.summary);

  return (
    <article className="pb-24 pt-36 sm:pt-44" data-testid={`project-detail-${p.slug}`}>
      <div className="container-x">
        <Link to="/projects" className="link-line inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-ash hover:text-ivory" data-testid="project-back-link">
          <ArrowLeft size={14} /> All Projects
        </Link>
        <div className="mt-12 flex items-center gap-4">
          <span className="eyebrow">{p.number} / {p.category}</span>
        </div>
        <MaskLines as="h1" onLoad delay={0.1} lines={[p.title]} className="mt-6 font-serif text-6xl font-light leading-[0.95] text-ivory sm:text-8xl lg:text-[9rem]" testId="project-detail-title" />
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.5, ease: EASE }} className="mt-10 max-w-2xl text-lg text-ash">
          {p.summary}
        </motion.p>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.7 }} className="mt-10 flex flex-wrap gap-4">
          <LinkOrPending href={p.links.live} label="Live Demo" Icon={ArrowUpRight} testId="project-live-link" />
          <LinkOrPending href={p.links.source} label="Source Code" Icon={Github} testId="project-source-link" />
        </motion.div>
      </div>

      <div ref={ref} className="container-x mt-20">
        <motion.div
          initial={{ clipPath: "inset(8% 8% 8% 8%)" }}
          animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
          transition={{ duration: 1.6, delay: 0.4, ease: EASE }}
          className="relative aspect-[16/10] overflow-hidden bg-coal sm:aspect-[16/8]"
        >
          <motion.img src={p.cover} alt={`${p.title} cover`} style={{ y, scale: 1.2 }} className={`h-full w-full ${p.coverClass || "object-cover"}`} />
        </motion.div>
      </div>

      <div className="container-x mt-24">
        <Block n="01" title="Overview" testId="project-overview">{p.overview}</Block>
        <Block n="02" title="Problem & Objective" testId="project-problem">{p.problem}</Block>
        <Block n="03" title="Approach & Solution" testId="project-approach">{p.approach}</Block>
        <Block n="04" title="Main Features" testId="project-features">
          <ul className="divide-y divide-line border-y border-line">
            {p.features.map((f, i) => (
              <li key={f} className="flex gap-6 py-4 text-ivory/90"><span className="font-mono text-xs text-gold">0{i + 1}</span>{f}</li>
            ))}
          </ul>
        </Block>
        <Block n="05" title="Technology Stack" testId="project-stack">
          <ul className="flex flex-wrap gap-3">
            {p.stack.map((s) => <li key={s} className="border border-line px-4 py-2 font-mono text-xs text-ivory/90">{s}</li>)}
          </ul>
        </Block>
      </div>

      <div className="container-x mt-10 grid grid-cols-1 gap-6 md:grid-cols-12" data-testid="project-gallery">
        {p.gallery.map((src, i) => (
          <Reveal key={src} delay={i * 0.1} className={`relative overflow-hidden bg-coal ${i === 0 ? "aspect-[16/10] md:col-span-12" : "aspect-[4/3] md:col-span-6"}`}>
            <img src={src} alt={`${p.title} visual ${i + 1}`} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1.4s] hover:scale-105" />
          </Reveal>
        ))}
      </div>

      <div className="container-x mt-16">
        <Block n="06" title="Challenges & Lessons" testId="project-challenges">{p.challenges}</Block>
      </div>

      <div className="container-x mt-10">
        <Link to={`/projects/${next.slug}`} className="group block border-t border-line pt-12" data-testid="project-next-link">
          <span className="eyebrow">Next Project</span>
          <span className="mt-4 flex items-center justify-between gap-6 font-serif text-5xl font-light text-ivory transition-colors duration-500 group-hover:text-gold sm:text-7xl">
            {next.title}
            <ArrowUpRight className="h-10 w-10 shrink-0 transition-transform duration-500 group-hover:-translate-y-2 group-hover:translate-x-2 sm:h-16 sm:w-16" strokeWidth={1} />
          </span>
        </Link>
      </div>
    </article>
  );
};

export default function ProjectDetail() {
  const { slug } = useParams();
  const p = getProject(slug);
  return p ? <Detail key={p.slug} p={p} /> : <NotFound />;
}
